import React, { useEffect, useState } from 'react';
import styles from './OocyteRetrieval.module.css';
import { ReactComponent as EmbryoTransferProcedure } from '../../../assets/SVG/Embryo Transfer Procedure.svg';
import Grid from '@mui/material/Grid';
import { getOocyteRetrieval, OocyteRetrievalData } from './OocyteRetrievalHelper';
import { useTranslation } from 'react-i18next';
import { convertUnderscoreToTitle } from '../../../utils/commonUtils';

interface OocyteRetrievalProps {
  cycle: string;
}

const OocyteRetrieval: React.FC<OocyteRetrievalProps> = ({ cycle }) => {
  const [OocyteRetrieval, setOocyteRetrievalData] = useState<OocyteRetrievalData | null>(null);

  const { t } = useTranslation('cycleOverview_config');

  useEffect(() => {
    if (cycle) {
      getOocyteRetrieval(cycle)
        .then((data) => {
          setOocyteRetrievalData(data);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error fetching Oocyte Retrieval data:', error);
        });
    }
  }, [cycle]);

  return (
    <div className={styles.container}>
      <Grid container spacing={3} alignItems="flex-start">
        {/* Image Section */}
        <Grid item xs={12} md={4} className={styles.embryoProcedure}>
          <EmbryoTransferProcedure />
        </Grid>

        {/* Text Data Section */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('RetrievalDate')}</strong>
                <p className={styles.customTextStyle}>{OocyteRetrieval?.opu_date || '-'}</p>
              </div>
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('RetrievalTime')}</strong>
                <p className={styles.customTextStyle}>{OocyteRetrieval?.start_time || '-'}</p>
              </div>
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('Clinician')}</strong>
                <p className={styles.customTextStyle}>{OocyteRetrieval?.clinician_name || '-'}</p>
              </div>
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('Embryologist')}</strong>
                <p className={styles.customTextStyle}>{OocyteRetrieval?.embryologist_name || '-'}</p>
              </div>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={6} sm={3} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('Nurse')}</strong>
                <p className={styles.customTextStyle}>{OocyteRetrieval?.nurse_name || '-'}</p>
              </div>
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('LeftOptimumFollicles')}</strong>
                <p className={styles.customTextStyle}>
                  {OocyteRetrieval?.optimum_follicles?.left_follicles ?? '-'}
                </p>
              </div>
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('RightOptimumFollicles')}</strong>
                <p className={styles.customTextStyle}>
                  {OocyteRetrieval?.optimum_follicles?.right_follicles ?? '-'}
                </p>
              </div>
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('LeftOocytesRetrieved')}</strong>
                <p className={styles.customTextStyle}>
                  {OocyteRetrieval?.oocytes_retrieved_left ?? '-'}
                </p>
              </div>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={6} sm={3} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('RightOocytesRetrieved')}</strong>
                <p className={styles.customTextStyle}>
                  {OocyteRetrieval?.oocytes_retrieved_right ?? '-'}
                </p>
              </div>
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerTextStyle}>{t('LevelOfDifficulty')}</strong>
                <p className={styles.customTextStyle}>{convertUnderscoreToTitle(OocyteRetrieval?.difficulty_level) || '-'}</p>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default OocyteRetrieval;
